import { useEffect, type CSSProperties } from "react";
import { cn } from "@/lib/utils";

export interface ComptaDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: number | string;
}

/** Drawer latéral droit (design system Woahda). */
export default function ComptaDrawer({
  open,
  onClose,
  title,
  children,
  width = 600,
}: ComptaDrawerProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    if (open) {
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }
  }, [open, onClose]);

  if (!open) return null;

  /** Les classes du type `w-[min(100vw,${n}px)]` ne sont pas vues par Tailwind au build → largeur ignorée. */
  const widthStyle: CSSProperties | undefined =
    typeof width === "number" ? { width: `min(100vw, ${width}px)` } : undefined;

  const widthClass =
    width === "100%" || width === "100vw"
      ? "w-full"
      : typeof width === "string"
        ? width
        : undefined;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label="Fermer le panneau"
        className="fixed inset-0 z-[300] animate-[compta-drawer-overlay_0.25s_ease-out] bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
        onKeyDown={(e) => e.key === "Enter" && onClose()}
      />
      <div
        style={widthStyle}
        className={cn(
          "fixed right-0 top-0 z-[301] flex h-full max-w-full flex-col overflow-hidden border-l border-[#e4e8ef] bg-white animate-[compta-drawer-slide_0.35s_cubic-bezier(0.4,0,0.2,1)]",
          widthClass
        )}
      >
        {title ? (
          <div className="flex-shrink-0 border-b border-[#e4e8ef] px-5 py-4">
            <h2 className="text-[14px] font-semibold text-slate-900">{title}</h2>
          </div>
        ) : null}
        {children}
      </div>
      <style>{`
        @keyframes compta-drawer-overlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes compta-drawer-slide {
          from { opacity: 0.8; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
